import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { getConsentStatus, type ConsentStatusResponse } from '../services/consent';

export default function ConsentSuccessPage() {
  const [searchParams] = useSearchParams();
  const consentId = searchParams.get('consent_id') || '';

  const [status, setStatus] = useState<ConsentStatusResponse | null>(null);
  const [loading, setLoading] = useState(!!consentId);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!consentId) {
      return;
    }

    let cancelled = false;

    async function fetchStatus() {
      try {
        const result = await getConsentStatus(consentId);
        if (!cancelled) {
          setStatus(result);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load consent status');
          setLoading(false);
        }
      }
    }

    void fetchStatus();

    return () => {
      cancelled = true;
    };
  }, [consentId]);

  if (!consentId) {
    return (
      <div className="max-w-lg mx-auto text-center space-y-4 py-12">
        <p className="text-gray-500">No consent ID provided.</p>
        <Link
          to="/banks"
          className="inline-block text-sm font-medium text-primary-600 hover:text-primary-700"
        >
          Go to Banks
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto space-y-6 py-8">
      <div className="bg-white rounded-xl shadow-sm border border-green-200 p-8 text-center space-y-4">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100">
          <svg
            className="w-8 h-8 text-green-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>

        <h1 className="text-2xl font-bold text-gray-900">Consent Created</h1>
        <p className="text-sm text-gray-500">
          Your PSD2 consent has been successfully created and authorized.
        </p>

        {loading && (
          <p className="text-sm text-gray-400">Loading consent details...</p>
        )}

        {error && (
          <p className="text-sm text-red-600">{error}</p>
        )}

        {status && (
          <div className="mt-4 bg-gray-50 rounded-lg p-4 text-left space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Consent ID</span>
              <span className="font-mono text-xs text-gray-700 break-all">
                {status.consent_id}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Status</span>
              <span className="font-medium text-green-700">{status.status}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Bank</span>
              <span className="text-gray-700">{status.aspsp}</span>
            </div>
            {status.scope.length > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Scope</span>
                <span className="text-gray-700">{status.scope.join(', ')}</span>
              </div>
            )}
            {status.authorized_at && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Authorized</span>
                <span className="text-gray-700">
                  {new Date(status.authorized_at).toLocaleString()}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-center gap-4">
        <Link
          to="/banks"
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Connect Another Bank
        </Link>
        <Link
          to="/consents"
          className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 transition-colors"
        >
          View All Consents
        </Link>
      </div>
    </div>
  );
}
